/**
 * CoordinatorAssign — Assign thread to a coordinator from thread panel
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from './ui/badge';
import { UserCheck, ChevronDown, Loader2, Check } from 'lucide-react';
import api from '../utils/api';
import { useTranslation } from 'react-i18next';
import { localisedRoleLabel, isSystemPoolAssignee } from '../utils/roleLabelI18n';


export const CoordinatorAssign = ({ thread, clinicId, participants, onUpdate }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // The canonical unassigned pool must read as "Unassigned" in the UI
  // language, never as the backend's internal pool name.
  const isSystemPool = isSystemPoolAssignee(thread);
  const assigneeLabel = isSystemPool
    ? t('provider.messaging.systemPoolBadge')
    : (thread?.assigned_agent_name
      || (thread?.assigned_agent_id
        ? t('provider.messaging.assignedRepresentativeFallback')
        : t('provider.messaging.assignAction')));

  // Build options from thread participants who are coordinators or sales_reps
  const assignableUsers = (participants || thread?.participants || []).filter(
    p => ['provider_coordinator', 'provider_sales_rep', 'provider_admin'].includes(p.role) && p.is_active
  );

  const handleAssign = async (userId) => {
    setOpen(false);
    setLoading(true);
    try {
      // Assignee display name is resolved server-side from canonical staff identity.
      const res = await api.put(`/v2/threads/${thread.id}/assign`, { agent_id: userId });
      onUpdate?.(res.data.thread);
    } catch (err) {
      console.error('Assign error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative shrink-0" data-testid="coordinator-assign">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors"
        data-testid="assign-trigger"
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <UserCheck className="h-3 w-3 text-teal-500" />
        )}
        {assigneeLabel}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border rounded-lg shadow-lg py-1 min-w-[180px] z-30" data-testid="assign-dropdown">
          {assignableUsers.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">{t('provider.messaging.noTeamMembers')}</div>
          ) : (
            assignableUsers.map(p => (
              <button
                key={p.user_id}
                onClick={() => handleAssign(p.user_id, p.name)}
                className={`w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50 flex items-center gap-2 ${
                  thread?.assigned_agent_id === p.user_id ? 'bg-slate-100 font-semibold' : 'text-slate-700'
                }`}
                data-testid={`assign-option-${p.user_id}`}
              >
                <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-[10px] font-bold">
                  {(p.name || localisedRoleLabel(p.role, t))?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  {/* Never render an account identifier as a name: the
                      server drops identifier-shaped names, so fall back to
                      the translated role label. */}
                  <span className="block">{p.name || localisedRoleLabel(p.role, t)}</span>
                  <span className="text-[10px] text-slate-400">{localisedRoleLabel(p.role, t)}</span>
                </div>
                {thread?.assigned_agent_id === p.user_id && <Check className="h-3 w-3 ml-auto text-teal-500" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CoordinatorAssign;
